/**
 * English, Simplified Chinese (zh, for the factory) and Traditional Chinese, Taiwan (zh-TW, for the
 * overseas office). The English text is the key: `t("Order now")` returns the line from i18n/zh.ts or
 * i18n/zh-TW.ts, and the English when there is no translation yet, so an untranslated page still reads.
 * `{n}`-style placeholders are filled from `vars`.
 *
 * The choice is saved per browser (instant on load) and on the server (GET/PUT /api/me/prefs), so it
 * follows the person to other devices. Switching re-mounts the app under LangProvider, so helpers that
 * aren't components (shortDate, stockStory, labels in useMemo) pick up the new language too.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UserPrefs } from "@march/shared";
import { apiGet, apiPut } from "./api.ts";
import { ZH } from "../i18n/zh.ts";
import { ZH_TW } from "../i18n/zh-TW.ts";

export type Lang = "en" | "zh" | "zh-TW";
const DICT: Record<Lang, Record<string, string> | null> = { en: null, zh: ZH, "zh-TW": ZH_TW };
const isLang = (v: unknown): v is Lang => v === "en" || v === "zh" || v === "zh-TW";

const KEY = "mia.lang";
let current: Lang = read();

function read(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    return isLang(v) ? v : "en";
  } catch {
    return "en";
  }
}

function apply(lang: Lang) {
  current = lang;
  document.documentElement.lang = lang === "zh" ? "zh-CN" : lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* not remembered in this browser; the server copy still is */
  }
}

/** Translate one line of interface text. */
export function t(en: string, vars?: Record<string, string | number>): string {
  const s = DICT[current]?.[en] ?? en;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

export const getLang = () => current;
/** For toLocaleString and friends. */
export const locale = () => (current === "zh" ? "zh-CN" : current === "zh-TW" ? "zh-TW" : undefined);

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: "en", setLang: () => {} });

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  const saved = useQuery<UserPrefs>({ queryKey: ["prefs"], queryFn: () => apiGet<UserPrefs>("/api/me/prefs"), staleTime: Infinity });

  // The server copy wins once, on load: someone who picked Chinese on another device gets it here too.
  useEffect(() => {
    const l = saved.data?.lang;
    if (isLang(l) && l !== current) {
      apply(l);
      setState(l);
    }
  }, [saved.data?.lang]);

  useEffect(() => apply(lang), [lang]);

  const setLang = useCallback((l: Lang) => {
    apply(l);
    setState(l);
    apiPut("/api/me/prefs", { lang: l }).catch(() => {
      /* kept in this browser; tried again next time they switch */
    });
  }, []);

  return (
    <Ctx.Provider value={{ lang, setLang }}>
      <div key={lang} style={{ display: "contents" }}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export const useLang = () => useContext(Ctx);

/** EN / 简体 / 繁體, in the top bar and on the choose-your-password screen. */
export function LangSwitch() {
  const { lang, setLang } = useLang();
  const opts: Array<[Lang, string]> = [["en", "EN"], ["zh", "简体"], ["zh-TW", "繁體"]];
  return (
    <span className="seg sm lang-switch" role="group" aria-label="Language / 语言 / 語言">
      {opts.map(([l, label]) => (
        <button key={l} type="button" lang={l === "en" ? "en" : l === "zh" ? "zh-CN" : "zh-TW"} aria-pressed={lang === l} onClick={() => setLang(l)}>
          {label}
        </button>
      ))}
    </span>
  );
}
